import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, Paper, Alert, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingAPI } from '../services/api';
import { toast } from 'react-toastify';

// Logo color palette
const logoColors = {
    primaryBlue: '#3f51b5',
    brightYellow: '#c4d835',
    darkBlue: '#2c387e',
    lightYellow: '#d4e157',
};

const CancelBookingPage = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [booking, setBooking] = useState(null);
    const [error, setError] = useState('');
    const [fetchingBooking, setFetchingBooking] = useState(true);

    useEffect(() => {
        const fetchBookingDetails = async () => {
            try {
                setFetchingBooking(true);
                const response = await bookingAPI.getBookingById(bookingId);
                setBooking(response.data);
            } catch (error) {
                setError('Failed to load booking details');
                console.error('Error fetching booking:', error);
            } finally {
                setFetchingBooking(false);
            }
        };

        if (bookingId) {
            fetchBookingDetails();
        }
    }, [bookingId]);

    const handleCancelBooking = async () => {
        if (booking && booking.status === 'CONFIRMED') {
            setError('Confirmed bookings cannot be cancelled. For further assistance, please contact us at booking@thegrid.pk or call +923238148153');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await bookingAPI.cancelBookingByUser(bookingId);
            toast.success('Booking cancelled successfully!');
            navigate('/');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to cancel booking';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate('/');
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Paper
                    elevation={8}
                    sx={{
                        p: 4,
                        width: '100%',
                        maxWidth: 500,
                        background: `linear-gradient(135deg, ${logoColors.primaryBlue}f0 0%, ${logoColors.darkBlue}f0 100%)`,
                        backdropFilter: 'blur(20px)',
                        border: `2px solid ${logoColors.brightYellow}40`,
                        borderRadius: 3,
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                color: '#f44336',
                                fontWeight: 'bold',
                                textShadow: `2px 2px 4px ${logoColors.darkBlue}`,
                            }}
                        >
                            Cancel Booking
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: logoColors.lightYellow,
                                mt: 1,
                            }}
                        >
                            The Grid - Booking Cancellation
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {fetchingBooking ? (
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <CircularProgress sx={{ color: logoColors.brightYellow, mb: 2 }} />
                            <Typography
                                variant="body1"
                                sx={{
                                    color: logoColors.brightYellow,
                                }}
                            >
                                Loading booking details...
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ mb: 3 }}>
                            {booking && booking.status === 'CONFIRMED' ? (
                                <>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: '#ff9800',
                                            textAlign: 'center',
                                            mb: 2,
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Booking Confirmed
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: logoColors.brightYellow,
                                            textAlign: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        Your booking has been confirmed and payment has been received.
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: logoColors.lightYellow,
                                            textAlign: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        Booking ID: {bookingId}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: logoColors.lightYellow,
                                            textAlign: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <strong>Note:</strong> Confirmed bookings cannot be cancelled. 
                                        For further assistance, please contact us at booking@thegrid.pk or call +923238148153.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: logoColors.brightYellow,
                                            textAlign: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        Are you sure you want to cancel your booking?
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: logoColors.lightYellow,
                                            textAlign: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        Booking ID: {bookingId}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: logoColors.lightYellow,
                                            textAlign: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <strong>Note:</strong> You can only cancel PENDING bookings. 
                                        Confirmed bookings must be cancelled by contacting admin.
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={handleGoBack}
                            disabled={loading}
                            sx={{
                                color: logoColors.brightYellow,
                                borderColor: logoColors.brightYellow,
                                '&:hover': {
                                    borderColor: logoColors.lightYellow,
                                    backgroundColor: `${logoColors.brightYellow}20`,
                                },
                            }}
                        >
                            Go Back
                        </Button>
                        {booking && booking.status !== 'CONFIRMED' && (
                            <Button
                                variant="contained"
                                onClick={handleCancelBooking}
                                disabled={loading}
                                sx={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#d32f2f',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    },
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                        Cancelling...
                                    </>
                                ) : (
                                    'Yes, Cancel Booking'
                                )}
                            </Button>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default CancelBookingPage;
